// TUT-551 — a ficha da pessoa da equipe na tela: as regras gêmeas das da API
// (`apps/api/src/domain/ficha-da-equipe.ts`), que a tela usa para avisar no
// campo antes de enviar e que o modo demonstração usa para decidir; o
// rascunho do formulário; a diferença que vira o PATCH parcial; e de que campo
// é a recusa do servidor. Quem decide é sempre o servidor.
import { mascaraDeTelefone } from "./perfil-do-escritorio";
import {
  TIPOS_DE_REGISTRO_PROFISSIONAL,
  type CamposDaFichaDaEquipe,
  type EntradaDaFichaDaEquipe,
  type RegistroProfissional,
  type TipoDeRegistroProfissional,
} from "./types";

export { TIPOS_DE_REGISTRO_PROFISSIONAL };

export const UFS_DO_BRASIL = [
  "AC", "AL", "AM", "AP", "BA", "CE", "DF", "ES", "GO", "MA", "MG", "MS", "MT", "PA",
  "PB", "PE", "PI", "PR", "RJ", "RN", "RO", "RR", "RS", "SC", "SE", "SP", "TO",
] as const;

export const ROTULO_DO_TIPO: Readonly<Record<TipoDeRegistroProfissional, string>> = {
  OAB: "OAB",
  CREA: "CREA",
  CRM: "CRM",
  CRQ: "CRQ",
  CFT: "CFT",
  OUTRO: "Outro",
};

const PADRAO_DO_TELEFONE = /^[1-9]{2}[0-9]{8,9}$/u;
const PADRAO_DO_NUMERO_DO_REGISTRO = /^[0-9A-Z./-]{1,20}$/u;
export const PRIMEIRA_DATA_DE_ENTRADA = "1900-01-01";
export const ULTIMA_DATA_DE_ENTRADA = "2100-12-31";

export const FICHA_VAZIA: Readonly<CamposDaFichaDaEquipe> = Object.freeze({
  preferredName: null,
  jobTitle: null,
  mobilePhone: null,
  phone: null,
  professionalRegistry: null,
  joinedOn: null,
});

export type ResultadoDaFicha = { ok: true; value: CamposDaFichaDaEquipe } | { ok: false; message: string };

class Recusa extends Error {}

function limpo(valor: string | null | undefined): string | null {
  const aparado = (valor ?? "").trim();
  return aparado ? aparado : null;
}

function texto(rotulo: string, valor: string | null | undefined, minimo: number, maximo: number): string | null {
  const t = limpo(valor);
  if (t === null) return null;
  // Em code points, como o `char_length` do CHECK: o `length` do JS conta um
  // emoji como dois, e o banco recusaria o que aqui passou (500 em vez de 400).
  const tamanho = [...t].length;
  if (tamanho < minimo || tamanho > maximo) throw new Recusa(`${rotulo}: de ${minimo} a ${maximo} caracteres.`);
  if (/[<>]/u.test(t)) throw new Recusa(`${rotulo}: sem os sinais < e >.`);
  return t;
}

function telefone(rotulo: string, valor: string | null | undefined): string | null {
  const t = limpo(valor);
  if (t === null) return null;
  const digitos = t.replace(/\D/gu, "");
  if (!PADRAO_DO_TELEFONE.test(digitos)) throw new Recusa(`${rotulo} com DDD: 10 ou 11 dígitos.`);
  return digitos;
}

function registro(valor: EntradaDaFichaDaEquipe["professionalRegistry"]): RegistroProfissional | null {
  if (valor === null || valor === undefined) return null;
  const kind = limpo(valor.kind)?.toUpperCase() ?? null;
  const numero = limpo(valor.number)?.replace(/\s/gu, "").toUpperCase() ?? null;
  const uf = limpo(valor.state)?.toUpperCase() ?? null;
  if (kind === null && numero === null && uf === null) return null;
  if (kind === null || !(TIPOS_DE_REGISTRO_PROFISSIONAL as readonly string[]).includes(kind)) {
    throw new Recusa(`Tipo do registro: ${TIPOS_DE_REGISTRO_PROFISSIONAL.join(", ")}.`);
  }
  if (numero === null || !PADRAO_DO_NUMERO_DO_REGISTRO.test(numero)) {
    throw new Recusa("Número do registro: até 20 letras, dígitos, ponto, barra ou hífen.");
  }
  if (uf === null || !(UFS_DO_BRASIL as readonly string[]).includes(uf)) {
    throw new Recusa("UF do registro: a sigla de um estado ou do DF.");
  }
  return { kind: kind as TipoDeRegistroProfissional, number: numero, state: uf };
}

function dataDeEntrada(valor: string | null | undefined): string | null {
  const t = limpo(valor);
  if (t === null) return null;
  const recusa = new Recusa("Data de entrada: uma data entre 01/01/1900 e 31/12/2100, no formato AAAA-MM-DD.");
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(t)) throw recusa;
  const [ano, mes, dia] = t.split("-").map(Number) as [number, number, number];
  const data = new Date(Date.UTC(ano, mes - 1, dia));
  if (data.getUTCFullYear() !== ano || data.getUTCMonth() !== mes - 1 || data.getUTCDate() !== dia) throw recusa;
  if (t < PRIMEIRA_DATA_DE_ENTRADA || t > ULTIMA_DATA_DE_ENTRADA) throw recusa;
  return t;
}

/** Aplica o PATCH sobre a ficha e confere: a mensagem do primeiro campo recusado, ou a ficha nova. */
export function aplicarNaFicha(atual: CamposDaFichaDaEquipe, input: EntradaDaFichaDaEquipe): ResultadoDaFicha {
  const veio = (campo: keyof EntradaDaFichaDaEquipe) =>
    Object.prototype.hasOwnProperty.call(input, campo) && input[campo] !== undefined;
  try {
    return {
      ok: true,
      value: {
        preferredName: veio("preferredName") ? texto("Nome de tratamento", input.preferredName, 2, 60) : atual.preferredName,
        jobTitle: veio("jobTitle") ? texto("Cargo ou função", input.jobTitle, 2, 80) : atual.jobTitle,
        mobilePhone: veio("mobilePhone") ? telefone("Celular", input.mobilePhone) : atual.mobilePhone,
        phone: veio("phone") ? telefone("Telefone", input.phone) : atual.phone,
        professionalRegistry: veio("professionalRegistry")
          ? registro(input.professionalRegistry)
          : atual.professionalRegistry
            ? { ...atual.professionalRegistry }
            : null,
        joinedOn: veio("joinedOn") ? dataDeEntrada(input.joinedOn) : atual.joinedOn,
      },
    };
  } catch (erro) {
    if (erro instanceof Recusa) return { ok: false, message: erro.message };
    throw erro;
  }
}

/** Os campos do formulário que recebem a recusa. */
export type CampoDaFicha =
  | "preferredName"
  | "jobTitle"
  | "mobilePhone"
  | "phone"
  | "registro.kind"
  | "registro.number"
  | "registro.state"
  | "joinedOn";

/** O começo de cada mensagem de recusa (o rótulo), na ordem em que a API confere. */
const ROTULOS_DA_RECUSA: ReadonlyArray<[string, CampoDaFicha]> = [
  ["Nome de tratamento", "preferredName"],
  ["Cargo ou função", "jobTitle"],
  ["Celular", "mobilePhone"],
  ["Telefone", "phone"],
  ["Tipo do registro", "registro.kind"],
  ["Número do registro", "registro.number"],
  ["UF do registro", "registro.state"],
  ["Data de entrada", "joinedOn"],
];

/** De que campo é a recusa (a mensagem começa pelo rótulo), ou `null` se não é de campo. */
export function campoDoErro(mensagem: string): CampoDaFicha | null {
  return ROTULOS_DA_RECUSA.find(([rotulo]) => mensagem.startsWith(rotulo))?.[1] ?? null;
}

/** O formulário: tudo texto, o vazio é "" (a tela nunca mostra "null"). */
export interface RascunhoDaFicha {
  preferredName: string;
  jobTitle: string;
  mobilePhone: string;
  phone: string;
  registroKind: string;
  registroNumber: string;
  registroState: string;
  joinedOn: string;
}

export function rascunhoDaFicha(ficha: CamposDaFichaDaEquipe): RascunhoDaFicha {
  return {
    preferredName: ficha.preferredName ?? "",
    jobTitle: ficha.jobTitle ?? "",
    mobilePhone: ficha.mobilePhone ? mascaraDeTelefone(ficha.mobilePhone) : "",
    phone: ficha.phone ? mascaraDeTelefone(ficha.phone) : "",
    registroKind: ficha.professionalRegistry?.kind ?? "",
    registroNumber: ficha.professionalRegistry?.number ?? "",
    registroState: ficha.professionalRegistry?.state ?? "",
    joinedOn: ficha.joinedOn ?? "",
  };
}

const digitos = (valor: string) => valor.replace(/\D/gu, "");
const ouNulo = (valor: string) => (valor.trim() ? valor.trim() : null);

/**
 * O PATCH parcial: só os campos que mudaram em relação ao gravado. O telefone
 * compara pelos dígitos (a máscara não é mudança); o registro vai inteiro, ou
 * `null` quando os três ficaram vazios.
 */
export function diferencaDaFicha(gravada: CamposDaFichaDaEquipe, rascunho: RascunhoDaFicha): EntradaDaFichaDaEquipe {
  const entrada: EntradaDaFichaDaEquipe = {};
  if (ouNulo(rascunho.preferredName) !== gravada.preferredName) entrada.preferredName = ouNulo(rascunho.preferredName);
  if (ouNulo(rascunho.jobTitle) !== gravada.jobTitle) entrada.jobTitle = ouNulo(rascunho.jobTitle);
  if ((digitos(rascunho.mobilePhone) || null) !== gravada.mobilePhone) entrada.mobilePhone = ouNulo(rascunho.mobilePhone);
  if ((digitos(rascunho.phone) || null) !== gravada.phone) entrada.phone = ouNulo(rascunho.phone);
  const registroVazio = !rascunho.registroKind && !rascunho.registroNumber.trim() && !rascunho.registroState;
  const antes = gravada.professionalRegistry;
  if (registroVazio) {
    if (antes) entrada.professionalRegistry = null;
  } else if (
    rascunho.registroKind !== antes?.kind ||
    rascunho.registroNumber.trim() !== antes.number ||
    rascunho.registroState !== antes.state
  ) {
    entrada.professionalRegistry = {
      kind: rascunho.registroKind,
      number: rascunho.registroNumber.trim(),
      state: rascunho.registroState,
    };
  }
  if ((rascunho.joinedOn || null) !== gravada.joinedOn) entrada.joinedOn = rascunho.joinedOn || null;
  return entrada;
}

/** O telefone com a máscara brasileira, ou `null`. */
export function mostrarTelefone(valor: string | null): string | null {
  return valor ? mascaraDeTelefone(valor) : null;
}

/** "CREA PR12345/D · PR", ou `null` sem registro. */
export function rotuloDoRegistro(valor: RegistroProfissional | null): string | null {
  return valor ? `${ROTULO_DO_TIPO[valor.kind]} ${valor.number} · ${valor.state}` : null;
}

/** As iniciais (até duas) de um nome, para o avatar sem foto. */
export function iniciaisDoNome(nome: string): string {
  return nome
    .split(/\s+/u)
    .filter(Boolean)
    .slice(0, 2)
    .map((parte) => parte[0]!.toUpperCase())
    .join("");
}
